import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { message } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useGetMeQuery } from '@/api/auth/api';
import {
  useAddOrganizationMutation,
  useGetAllOrganizationsQuery,
  useGetOrganizationsTreeQuery,
} from '@/api/organizations/api';
import { useGetRolesQuery } from '@/api/roles/api';
import { RoleResponse } from '@/api/roles/types';
import { organizationColumns } from '@/pages/Administration/components/organizations/columns.config';
import { createRegionOrgModalSchema } from '@/pages/Administration/components/organizations/modal.config';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { hasPermission } from '@/shared/security/hasPermission';
import { administration } from '@/shared/security/permissions/administration';
import { removeEmptyChildren } from '@/utils/utils';

export const OrganizationsTable = () => {
  const navigate = useNavigate();
  const [parentId, setParentId] = useState<string | undefined>(undefined);
  const [isOpenCreateRegionOrgModal, setIsOpenCreateRegionOrgModal] = useState(false);

  const {
    data: organizations,
    isLoading: organizationsLoading,
    isFetching: organizationsFetching,
  } = useGetOrganizationsTreeQuery({ level: OrganizationLevelType.ORGANIZATION });

  const { data: user } = useGetMeQuery();
  const { data: allOrganizations } = useGetAllOrganizationsQuery({});
  const { data: allRoles } = useGetRolesQuery(
    {},
    {
      skip: !hasPermission(user.auth, { permissions: administration.orgs.addDepartment }),
    },
  );

  const [addOrganization] = useAddOrganizationMutation();

  const onCreateRegion = (record: any) => {
    setParentId(record?.id);
    setIsOpenCreateRegionOrgModal(true);
  };

  const createOrganization = async (payload: any) => {
    await addOrganization({
      ...payload,
    }).unwrap();

    setIsOpenCreateRegionOrgModal(false);
    message.success('Организация успешно сохранена');
  };

  return (
    <>
      <PageTitle title={'Организации'} />
      <Modal
        mode={'form'}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        data={{ parentId: parentId }}
        open={isOpenCreateRegionOrgModal}
        onCancel={() => {
          setIsOpenCreateRegionOrgModal(false);
          setParentId(undefined);
        }}
        onSubmit={(payload) => createOrganization(payload)}
        title={'Добавить организацию районного уровня'}
        schema={createRegionOrgModalSchema(allOrganizations, allRoles as RoleResponse[])}
      />
      <UTable
        scroll={{ y: 55 * 9 }}
        loading={organizationsLoading || organizationsFetching}
        size={'small'}
        data={removeEmptyChildren(organizations)}
        columns={organizationColumns(allOrganizations)}
        expandable={{
          childrenColumnName: 'children',
        }}
        actions={(record) => [
          ...(record.level !== OrganizationLevelType.REGION
            ? [
                {
                  icon: <EyeOutlined />,
                  action: (record: any) =>
                    navigate(`/administration/organizations/${record?.id}`),
                  security: {
                    permissions: administration.orgs.view,
                  },
                },
              ]
            : []),

          ...(record.level === OrganizationLevelType.REGION
            ? [
                {
                  tooltip: 'Добавить организацию на районном уровне',
                  icon: <PlusOutlined />,
                  action: () => onCreateRegion(record),
                  security: {
                    permissions: administration.orgs.add,
                  },
                },
              ]
            : []),
        ]}
      />
    </>
  );
};
